import React, { useState, useEffect, useRef } from "react";
import { User, Mail, Briefcase, Smile, Edit, Eye, LogOut, Github, Linkedin, Trash2, Check } from 'lucide-react';
import ImageCropperModal from './ImageCropperModal';
import { getCroppedImage } from '../utils/imageCropperHelper';
import { isEqual } from 'lodash';

// --- HELPER 1: File to Base64 ---
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

// --- HELPER 2: Normalize Data (Finds data wherever it hides) ---
const normalizeData = (data) => {
  if (!data) return {};
  return {
    userName: data.userName || "",
    userEmail: data.userEmail || "",
    userGender: data.userGender || "",
    // Check all possible keys for these fields
    bio: data.bio || data.about || data.description || "",
    workProfile: data.workProfile || data.collegeName || data.company || "",
    role: data.userRole || data.role || "",
    profilePhoto: (data.profilePhoto || data.userProfile) || null,
    githubAcc: data.githubAcc || data.github || data.githubLink || "",
    linkedinAcc: data.linkedinAcc || data.linkedin || data.linkedinLink || ""
  };
};

const ProfilePage = ({ profile, setProfile, handleLogout, refreshProfile }) => {
  // --- STATE ---
  const [isEditing, setIsEditing] = useState(false);
  const [originalProfileData, setOriginalProfileData] = useState({});
  const [profileData, setProfileData] = useState({});
  
  const [photoToCrop, setPhotoToCrop] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  // --- REFS ---
  const justSaved = useRef(false);
  const photoUrlRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // --- 1. INITIAL LOAD & REFRESH HANDLER ---
  useEffect(() => {
    // A. Try to load from LocalStorage first (The Backup Plan)
    const savedCache = localStorage.getItem("user_profile_cache");
    let cachedData = savedCache ? JSON.parse(savedCache) : {};

    // B. Clean the incoming props
    const incomingData = normalizeData(profile);

    // C. Merge: Incoming Props > Cache > Empty
    // We prioritize Props, but if Props are empty, we use Cache.
    const mergedData = {
      userName: incomingData.userName || cachedData.userName || "",
      userEmail: incomingData.userEmail || cachedData.userEmail || "",
      userGender: incomingData.userGender || cachedData.userGender || "",
      bio: incomingData.bio || cachedData.bio || "",
      workProfile: incomingData.workProfile || cachedData.workProfile || "",
      role: incomingData.role || cachedData.role || "",
      profilePhoto: incomingData.profilePhoto || cachedData.profilePhoto || null,
      githubAcc: incomingData.githubAcc || cachedData.githubAcc || "",
      linkedinAcc: incomingData.linkedinAcc || cachedData.linkedinAcc || ""
    };

    // Prevent overwriting if we just saved
    if (justSaved.current) {
        justSaved.current = false;
        return;
    }

    // Only update state if we found REAL data
    if (Object.keys(mergedData).length > 0) {
        // If the props are totally empty (fresh refresh), don't wipe the state
        const hasPropsData = Object.values(incomingData).some(val => val !== "" && val !== null);
        
        if (hasPropsData || savedCache) {
            setOriginalProfileData(mergedData);
            setProfileData(mergedData);
            photoUrlRef.current = mergedData.profilePhoto;
        }
    }

    return () => {
      if (photoUrlRef.current && photoUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(photoUrlRef.current);
      }
    };
  }, [profile]); // Depend on profile updates

  const hasChanges = !isEqual(profileData, originalProfileData) || croppedImageBlob !== null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
  };

  const handleRemovePhoto = () => {
    if (window.confirm("Are you sure you want to remove your profile photo?")) {
      setProfileData(prev => ({ ...prev, profilePhoto: null }));
      setCroppedImageBlob(null); 
    }
  };

  // --- 2. SAVE HANDLER (The "Shotgun" Approach) ---
  const handleSave = async () => {
    if (hasChanges && !isSubmitting) {
      setIsSubmitting(true);
      try {
        let finalImage = null; 

        if (profileData.profilePhoto === null && originalProfileData.profilePhoto !== null) {
            finalImage = ""; 
        } else if (croppedImageBlob) {
            finalImage = await fileToBase64(croppedImageBlob);
        }

        // PREPARE PAYLOAD: Send EVERY possible key to ensure backend catches it
        const payload = {
          // Identifiers
          userName: profileData.userName,
          userEmail: profileData.userEmail,
          userGender: profileData.userGender,
          
          // Role & Work (Send to all 3 common columns)
          userRole: profileData.role,
          role: profileData.role,
          collegeName: profileData.workProfile, 
          company: profileData.workProfile,     
          workProfile: profileData.workProfile, 
          
          // Photo
          userProfile: finalImage,
          
          // Socials (Send to both variations)
          githubAcc: profileData.githubAcc,
          github: profileData.githubAcc,
          linkedinAcc: profileData.linkedinAcc,
          linkedin: profileData.linkedinAcc,
          
          // Bio (Send to both variations)
          bio: profileData.bio,
          about: profileData.bio,
          description: profileData.bio
        };

        const response = await fetch("http://localhost:3453/api/v1/signups/update-profile", {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const updatedUser = data.data;

          const newPhotoState = (updatedUser.userProfile && updatedUser.userProfile !== "")
            ? updatedUser.userProfile 
            : (profileData.profilePhoto); // Fallback to local if backend returns nothing

          // 1. UPDATE LOCAL STATE
          const newState = {
            ...profileData,
            profilePhoto: newPhotoState
          };
          setProfileData(newState);
          setOriginalProfileData(newState);
          justSaved.current = true;

          // 2. SAVE TO LOCALSTORAGE (This fixes the refresh issue!)
          localStorage.setItem("user_profile_cache", JSON.stringify(newState));

          // 3. UPDATE PARENT STATE (Shotgun style here too)
          if (setProfile) {
              setProfile(prev => ({
                 ...prev,
                 ...newState,
                 // Force generic keys too
                 userProfile: newState.profilePhoto,
                 about: newState.bio,
                 collegeName: newState.workProfile,
                 company: newState.workProfile,
                 github: newState.githubAcc,
                 linkedin: newState.linkedinAcc
              }));
          }

          setCroppedImageBlob(null);
          setIsEditing(false);
          
          // 4. Force Backend Refresh
          if (refreshProfile) {
             refreshProfile(); 
          }

          alert("Profile updated successfully!");
        } else {
          throw new Error(data.message || "Update failed");
        }
      } catch (error) {
        console.error("Error updating profile:", error);
        alert("Failed to update profile.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => setPhotoToCrop(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async (croppedAreaPixels) => {
    try {
      const croppedImage = await getCroppedImage(photoToCrop, croppedAreaPixels);
      const imageUrl = URL.createObjectURL(croppedImage);
      
      if (photoUrlRef.current && photoUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(photoUrlRef.current);
      }
      
      photoUrlRef.current = imageUrl;
      setProfileData(prev => ({ ...prev, profilePhoto: imageUrl }));
      setCroppedImageBlob(croppedImage);
      setPhotoToCrop(null);
    } catch (e) { console.error(e); }
  };

  const getInitials = (name) => {
    if (!name) return "U";
    const parts = name.trim().split(" ");
    return parts.length > 1 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name[0].toUpperCase();
  };

  // --- RENDER FUNCTIONS ---

  const renderEditable = () => (
    <>
      <div className="profile-photo-container">
        {profileData.profilePhoto ? (
          <img src={profileData.profilePhoto} alt="Profile" className="profile-photo" />
        ) : (
          <div className="profile-initials">{getInitials(profileData.userName)}</div>
        )}
        <div className="photo-actions">
            <label htmlFor="photo-upload" className="change-photo-btn">
              <Edit size={18} color="white" />
              <input id="photo-upload" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
            </label>
            {profileData.profilePhoto && (
              <button type="button" className="remove-photo-btn" onClick={handleRemovePhoto}>
                <Trash2 size={18} color="white" />
              </button>
            )}
        </div>
      </div>
      
      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><Mail size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Email</div>
          <input type="email" value={profileData.userEmail || ""} disabled className="disabled-input" />
        </div>
      </div>

      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><User size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Username</div>
          <input type="text" name="userName" value={profileData.userName || ""} onChange={handleInputChange} />
        </div>
      </div>

      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><Smile size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Bio</div>
          <textarea name="bio" value={profileData.bio || ""} onChange={handleInputChange} placeholder="Tell us about yourself..." />
        </div>
      </div>

      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><Briefcase size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Work/Education Profile</div>
          <select name="role" value={profileData.role || ""} onChange={handleInputChange}>
            <option value="">Select Role...</option>
            <option value="Student">Student</option>
            <option value="Job">Job</option>
            <option value="Intern">Intern</option>
          </select>
          <input 
            type="text" 
            name="workProfile" 
            value={profileData.workProfile || ""} 
            onChange={handleInputChange} 
            placeholder={profileData.role === "Student" ? "Enter University Name" : "Enter Company Name"} 
            style={{marginTop: '0.5rem'}}
          />
        </div>
      </div>

      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><Github size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">GitHub Profile</div>
          <input 
            type="text" 
            name="githubAcc" 
            value={profileData.githubAcc || ""} 
            onChange={handleInputChange} 
            placeholder="GitHub Profile URL" 
          />
        </div>
      </div>

      <div className="info-item full-width no-hover">
        <div className="icon-wrapper"><Linkedin size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">LinkedIn Profile</div>
          <input 
            type="text" 
            name="linkedinAcc" 
            value={profileData.linkedinAcc || ""} 
            onChange={handleInputChange} 
            placeholder="LinkedIn Profile URL" 
          />
        </div>
      </div>
    </>
  );

  const renderView = () => (
    <>
      <div className="profile-photo-container">
        {profileData.profilePhoto ? (
          <img src={profileData.profilePhoto} alt="Profile" className="profile-photo" />
        ) : (
          <div className="profile-initials">{getInitials(profileData.userName)}</div>
        )}
      </div>
      <h1 className="user-name-title">{profileData.userName}</h1>
      
      <div className="info-item full-width">
        <div className="icon-wrapper"><User size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Username</div>
          <div className="info-value">{profileData.userName}</div>
        </div>
      </div>
      
      <div className="info-item full-width">
        <div className="icon-wrapper"><Smile size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">Bio</div>
          <div className="info-value">{profileData.bio || "No bio provided"}</div>
        </div>
      </div>
      
      <div className="info-item full-width">
        <div className="icon-wrapper"><Github size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">GitHub</div>
          <div className="info-value">{profileData.githubAcc || "Not shared"}</div>
        </div>
      </div>

      <div className="info-item full-width">
        <div className="icon-wrapper"><Linkedin size={22} className="sidebar-icon-style" /></div>
        <div className="info-content">
          <div className="info-label">LinkedIn</div>
          <div className="info-value">{profileData.linkedinAcc || "Not shared"}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      <style>{`
        .profile-container { max-width: 800px; margin: 0 auto; padding: 2.5rem; background: #111113; border-radius: 1.2rem; color: #fff; position: relative; display: flex; flex-direction: column; align-items: center; gap: 1.5rem; border: 1px solid #222; }
        .profile-photo-container { position: relative; width: 130px; height: 130px; margin-bottom: 0.5rem; }
        .profile-photo, .profile-initials { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; border: 3px solid #333; display: flex; align-items: center; justify-content: center; background: #1c1c1f; font-size: 3.5rem; overflow: hidden; color: #fff; }
        .photo-actions { position: absolute; bottom: 0; right: -10px; display: flex; flex-direction: column; gap: 8px; }
        .change-photo-btn, .remove-photo-btn { background: #0060ff; padding: 0.6rem; border-radius: 50%; cursor: pointer; z-index: 2; border: none; transition: 0.3s; display: flex; align-items: center; justify-content: center; }
        .remove-photo-btn { background: #ff4d4d; }
        .change-photo-btn:hover, .remove-photo-btn:hover { transform: scale(1.1); }
        .sidebar-icon-style { color: #cfcfd6; }
        .icon-wrapper { width: 40px; display: flex; justify-content: center; align-items: center; flex-shrink: 0; }
        .info-item { display: flex; align-items: flex-start; gap: 0.5rem; padding: 1.2rem; background: transparent; border: 1px solid transparent; border-radius: 0.8rem; width: 100%; box-sizing: border-box; transition: all 0.3s ease; }
        .info-item:not(.no-hover):hover { background: rgba(255, 255, 255, 0.05); border-color: #333; }
        .info-content { display: flex; flex-direction: column; flex-grow: 1; gap: 0.4rem; width: 100%; }
        .info-label { font-size: 0.85rem; color: #888; }
        .info-value { font-size: 1.05rem; color: #eee; word-break: break-word; }
        input, textarea, select { background: #1c1c1f; border: 1px solid #333; color: #fff; padding: 0.75rem; border-radius: 0.6rem; width: 100%; box-sizing: border-box; }
        .disabled-input { opacity: 0.5; cursor: not-allowed; }
        .action-buttons { display: flex; gap: 1rem; margin-top: 2rem; width: 100%; justify-content: center; flex-wrap: wrap; }
        .sidebar-style-btn { padding: 0.8rem 1.8rem; border-radius: 0.6rem; border: 1px solid transparent; cursor: pointer; display: flex; align-items: center; color: #fff; transition: 0.3s; font-weight: 500; background: transparent; }
        .sidebar-style-btn:hover { background: rgba(255, 255, 255, 0.08); border-color: #444; }
        .save-btn-sidebar { position: absolute; top: 1.5rem; right: 1.5rem; padding: 0.6rem 1.4rem; border-radius: 0.6rem; border: 1px solid transparent; cursor: pointer; color: #fff; font-weight: bold; background: transparent; transition: 0.3s; }
        .save-btn-sidebar.active:hover { background: rgba(255, 255, 255, 0.08); border-color: #444; }
        .save-btn-sidebar.disabled { opacity: 0.3; cursor: not-allowed; }
        .mobile-save-btn { position: absolute; top: 1rem; right: 1rem; background: #4ade80; color: #111; border: none; width: 42px; height: 42px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; box-shadow: 0 4px 12px rgba(74, 222, 128, 0.4); z-index: 10; }
        .mobile-save-btn:disabled { opacity: 0.5; background: #555; box-shadow: none; cursor: not-allowed; }
        .logout-btn { background: #ff4d4d; border: none; padding: 0.8rem 1.8rem; border-radius: 0.6rem; color: white; cursor: pointer; font-weight: 600; }
        .user-name-title { font-size: 1.8rem; font-weight: 700; margin: 0; }
        @media (max-width: 768px) { .profile-container { padding: 1.5rem; } .info-item { flex-direction: row; align-items: flex-start; } .action-buttons { flex-direction: column; width: 100%; } .sidebar-style-btn, .logout-btn { width: 100%; justify-content: center; } }
      `}</style>

      <div className="profile-container">
        {isEditing && (
            <>
                {isMobile ? (
                    <button className="mobile-save-btn" onClick={handleSave} disabled={!hasChanges || isSubmitting} title="Save Changes">
                        <Check size={24} />
                    </button>
                ) : (
                    <button className={`save-btn-sidebar ${hasChanges ? 'active' : 'disabled'}`} onClick={handleSave} disabled={!hasChanges || isSubmitting}>
                        {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                )}
            </>
        )}
        
        {isEditing ? renderEditable() : renderView()}
        
        <div className="action-buttons">
          {!isEditing ? (
            <button className="sidebar-style-btn" onClick={() => setIsEditing(true)}>
              <Edit size={18} style={{ marginRight: "10px" }} /> Edit Profile
            </button>
          ) : (
            <button className="sidebar-style-btn" onClick={() => setIsEditing(false)}>
              <Eye size={18} style={{ marginRight: "10px" }} /> Cancel View
            </button>
          )}
          <button className="logout-btn" onClick={handleLogout}>
            <LogOut size={18} style={{ marginRight: "10px" }} /> Logout
          </button>
        </div>
      </div>

      {photoToCrop && (
        <ImageCropperModal
          photo={photoToCrop}
          onCropComplete={handleCropComplete}
          onCancel={() => setPhotoToCrop(null)}
        />
      )}
    </>
  );
};

export default ProfilePage;